import { computeScore } from '@core/score';
import { getState, scoreActions } from '@core/store';
import { initAuth, signOut } from '@services/auth';
import { startSessionWatcher } from '@services/notifications';
import { Modal } from '@ui/components/Modal';
import { ToastContainer } from '@ui/components/Toast';
import { type Route, navigate, useRoute } from '@ui/lib/router';
import { useAuth, useSync } from '@ui/lib/useAuth';
import { useStore } from '@ui/lib/useStore';
import { AgendaScreen } from '@ui/screens/AgendaScreen';
import { AnalyticsScreen } from '@ui/screens/AnalyticsScreen';
import { ContentsScreen } from '@ui/screens/ContentsScreen';
import { DesignScreen } from '@ui/screens/DesignScreen';
import { GoalsScreen } from '@ui/screens/GoalsScreen';
import { HomeScreen } from '@ui/screens/HomeScreen';
import { LoginScreen } from '@ui/screens/LoginScreen';
import { PomodoroScreen } from '@ui/screens/PomodoroScreen';
import { ScoreScreen } from '@ui/screens/ScoreScreen';
import { SettingsScreen } from '@ui/screens/SettingsScreen';
import { useEffect, useState } from 'preact/hooks';

interface NavLink {
  route: Route;
  icon: string;
  label: string;
  badge?: string;
}

const NAV: NavLink[] = [
  { route: 'home', icon: '◉', label: 'Início' },
  { route: 'pomodoro', icon: '▶', label: 'Pomodoro' },
  { route: 'agenda', icon: '▦', label: 'Agenda', badge: 'NOVO' },
  { route: 'contents', icon: '▤', label: 'Conteúdos' },
  { route: 'goals', icon: '🎯', label: 'Metas' },
  { route: 'score', icon: '◈', label: 'Score' },
  { route: 'analytics', icon: '▰', label: 'Analytics' },
  { route: 'settings', icon: '⚙', label: 'Configurações' },
];

function renderScreen(route: Route) {
  switch (route) {
    case 'home':
      return <HomeScreen />;
    case 'agenda':
      return <AgendaScreen />;
    case 'contents':
      return <ContentsScreen />;
    case 'pomodoro':
      return <PomodoroScreen />;
    case 'goals':
      return <GoalsScreen />;
    case 'score':
      return <ScoreScreen />;
    case 'analytics':
      return <AnalyticsScreen />;
    case 'settings':
      return <SettingsScreen />;
    case 'login':
      return <LoginScreen />;
    case 'design':
      return <DesignScreen />;
    default:
      return <HomeScreen />;
  }
}

// ===== ATALHOS DE TECLADO (desktop) =====
const SHORTCUT_ROUTES: Route[] = [
  'home',
  'pomodoro',
  'agenda',
  'contents',
  'goals',
  'score',
  'analytics',
  'settings',
];

const SHORTCUT_HELP: { keys: string; desc: string }[] = [
  {
    keys: '1 – 8',
    desc: 'navegar: Início · Pomodoro · Agenda · Conteúdos · Metas · Score · Analytics · Config',
  },
  { keys: 'Espaço', desc: 'play / pause do ciclo (na tela Pomodoro)' },
  { keys: '?', desc: 'mostra / esconde esta ajuda' },
  { keys: 'Esc', desc: 'fecha modais' },
];

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

function HelpModal(props: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={props.open} onClose={props.onClose} title="Atalhos de teclado" maxWidth={480}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {SHORTCUT_HELP.map((s) => (
          <div key={s.keys} style={{ display: 'flex', gap: '14px', alignItems: 'baseline' }}>
            <kbd
              style={{
                fontFamily: 'var(--f-mono)',
                fontSize: '11px',
                color: 'var(--cyan)',
                background: 'rgba(0,240,255,0.08)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                padding: '3px 8px',
                whiteSpace: 'nowrap',
                minWidth: '64px',
                textAlign: 'center',
              }}
            >
              {s.keys}
            </kbd>
            <span style={{ fontSize: '13px', color: 'var(--text-soft)', lineHeight: 1.5 }}>
              {s.desc}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

const SYNC_DOT: Record<string, { color: string; label: string }> = {
  off: { color: 'var(--text-mute)', label: 'local' },
  syncing: { color: 'var(--amber)', label: 'sincronizando…' },
  synced: { color: 'var(--green)', label: 'sincronizado' },
  error: { color: 'var(--red)', label: 'erro de sync' },
};

function AccountBox() {
  const { user, ready } = useAuth();
  const sync = useSync();

  if (!ready) return null;

  if (!user) {
    return (
      <button
        type="button"
        onClick={() => navigate('login')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          padding: '10px 12px',
          marginBottom: '12px',
          background: 'rgba(255,215,0,0.06)',
          border: '1px solid rgba(255,215,0,0.35)',
          borderRadius: 'var(--r-md)',
          cursor: 'pointer',
          color: 'var(--gold)',
          fontFamily: 'var(--f-mono)',
          fontSize: '10px',
          letterSpacing: '1.2px',
          fontWeight: 700,
        }}
      >
        ⚡ ENTRAR / SINCRONIZAR
      </button>
    );
  }

  const dot = SYNC_DOT[sync.status] ?? SYNC_DOT.off;
  return (
    <div
      style={{
        padding: '10px 12px',
        marginBottom: '12px',
        background: 'rgba(0,8,24,0.5)',
        border: '1px solid var(--border-soft)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          title={sync.detail || dot.label}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: dot.color,
            boxShadow: `0 0 6px ${dot.color}`,
            flexShrink: 0,
            animation: sync.status === 'syncing' ? 'pulse 1.2s ease-in-out infinite' : 'none',
          }}
        />
        <span
          style={{
            flex: 1,
            fontFamily: 'var(--f-mono)',
            fontSize: '10px',
            color: 'var(--text-soft)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={user.email}
        >
          {user.email}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '6px',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '9px',
            color: dot.color,
            letterSpacing: '1px',
          }}
        >
          {dot.label.toUpperCase()}
        </span>
        <button
          type="button"
          onClick={() => {
            if (confirm('Sair? Os dados continuam neste browser e na nuvem.')) signOut();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--text-dim)',
            fontFamily: 'var(--f-mono)',
            fontSize: '9px',
            cursor: 'pointer',
            letterSpacing: '1px',
          }}
        >
          SAIR →
        </button>
      </div>
    </div>
  );
}

function ScoreBadge() {
  const state = useStore((s) => s);
  const { overall } = computeScore(state);

  return (
    <button
      type="button"
      onClick={() => navigate('score')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '12px',
        margin: '4px 0 12px',
        background: 'linear-gradient(135deg, rgba(0,240,255,0.08), rgba(177,74,255,0.06))',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        cursor: 'pointer',
        transition: 'all var(--tr-fast)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--f-heading)',
          fontSize: '26px',
          fontWeight: 900,
          lineHeight: 1,
          background: 'linear-gradient(180deg, var(--cyan), var(--purple))',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          minWidth: '44px',
          textAlign: 'center',
        }}
      >
        {overall ?? '—'}
      </div>
      <div style={{ textAlign: 'left' }}>
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '9px',
            letterSpacing: '1.5px',
            color: 'var(--cyan)',
          }}
        >
          PERFORMANCE
        </div>
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '9px',
            letterSpacing: '1.5px',
            color: 'var(--text-dim)',
          }}
        >
          SCORE
        </div>
      </div>
    </button>
  );
}

// ===== NAVEGAÇÃO MOBILE (<900px) =====
const TAB_ITEMS: { route: Route; icon: string; label: string }[] = [
  { route: 'home', icon: '◉', label: 'Início' },
  { route: 'pomodoro', icon: '▶', label: 'Foco' },
  { route: 'agenda', icon: '▦', label: 'Agenda' },
  { route: 'contents', icon: '▤', label: 'Conteúdos' },
];
const MORE_ITEMS: { route: Route; icon: string; label: string }[] = [
  { route: 'goals', icon: '🎯', label: 'Metas' },
  { route: 'score', icon: '◈', label: 'Score' },
  { route: 'analytics', icon: '▰', label: 'Analytics' },
  { route: 'settings', icon: '⚙', label: 'Configurações' },
];

function MobileTopBar() {
  const state = useStore((s) => s);
  const { overall } = computeScore(state);
  const sync = useSync();
  const dot = SYNC_DOT[sync.status] ?? SYNC_DOT.off;
  return (
    <header class="mobile-top mobile-only">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div class="brand-mark">RX</div>
        <span class="brand-title" style={{ fontSize: '12px' }}>
          ROBOX
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span
          class="sync-dot"
          title={sync.detail || dot.label}
          style={{ background: dot.color, boxShadow: `0 0 6px ${dot.color}` }}
        />
        <button type="button" class="score-chip" onClick={() => navigate('score')}>
          {overall ?? '—'} <small>SCORE</small>
        </button>
      </div>
    </header>
  );
}

function MobileTabBar(props: { route: Route; moreOpen: boolean; onMore: () => void }) {
  const moreActive = MORE_ITEMS.some((m) => m.route === props.route);
  return (
    <nav class="mobile-tabs mobile-only">
      {TAB_ITEMS.map((t) => (
        <button
          key={t.route}
          type="button"
          class={`tab ${props.route === t.route && !props.moreOpen ? 'active' : ''}`}
          onClick={() => navigate(t.route)}
        >
          <span class="ico">{t.icon}</span>
          {t.label}
        </button>
      ))}
      <button
        type="button"
        class={`tab ${moreActive || props.moreOpen ? 'active' : ''}`}
        onClick={props.onMore}
      >
        <span class="ico">⋯</span>
        Mais
      </button>
    </nav>
  );
}

function MoreSheet(props: { open: boolean; route: Route; onClose: () => void }) {
  const { user } = useAuth();
  const sync = useSync();
  if (!props.open) return null;
  const dot = SYNC_DOT[sync.status] ?? SYNC_DOT.off;
  return (
    <>
      <div class="more-sheet-overlay" onClick={props.onClose} />
      <div class="more-sheet">
        <div class="grab" />
        {MORE_ITEMS.map((m) => (
          <button
            key={m.route}
            type="button"
            class={`nav-item ${props.route === m.route ? 'active' : ''}`}
            onClick={() => {
              navigate(m.route);
              props.onClose();
            }}
          >
            <span class="ico">{m.icon}</span>
            <span>{m.label}</span>
          </button>
        ))}
        {user && (
          <div
            style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid var(--border-soft)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: dot.color,
                boxShadow: `0 0 6px ${dot.color}`,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                flex: 1,
                fontFamily: 'var(--f-mono)',
                fontSize: '11px',
                color: 'var(--text-soft)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </span>
            <button
              class="btn sm danger"
              onClick={() => {
                if (confirm('Sair da conta? Os dados continuam na nuvem.')) signOut();
              }}
            >
              SAIR
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Splash() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
      }}
    >
      <div class="brand-mark" style={{ width: '52px', height: '52px', fontSize: '20px' }}>
        RX
      </div>
      <div
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: '10px',
          color: 'var(--text-dim)',
          letterSpacing: '2.5px',
          animation: 'pulse 1.2s ease-in-out infinite',
        }}
      >
        CARREGANDO…
      </div>
    </div>
  );
}

export function App() {
  const { route } = useRoute();
  const { user, ready } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // auth: resolve sessão + liga o sync quando logado
  useEffect(() => {
    initAuth();
  }, []);

  // vigia de sessões planejadas → notificação "sessão em 5 min"
  useEffect(() => {
    if (!user) return;
    return startSessionWatcher();
  }, [user]);

  // atalhos globais de teclado (F1-23)
  useEffect(() => {
    if (!user) return;
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen((v) => !v);
        return;
      }
      if (e.key === ' ') {
        // play/pause só na tela do pomodoro (lá dentro alguém escuta)
        if (window.location.hash.startsWith('#/pomodoro')) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('robox:toggle-timer'));
        }
        return;
      }
      const idx = Number.parseInt(e.key, 10) - 1;
      if (idx >= 0 && idx < SHORTCUT_ROUTES.length) {
        navigate(SHORTCUT_ROUTES[idx]);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [user]);

  // logado na rota /login → manda pra home
  useEffect(() => {
    if (ready && user && route === 'login') navigate('home');
  }, [ready, user, route]);

  // fecha a folha "mais" ao navegar
  useEffect(() => {
    setMoreOpen(false);
  }, [route]);

  // snapshot diário do score (idempotente por data)
  useEffect(() => {
    const timer = setTimeout(() => {
      const state = getState();
      const { overall, subs } = computeScore(state);
      if (overall === null) return;
      const today = new Date().toISOString().slice(0, 10);
      const existing = state.scoreLogs.find((l) => l.date === today);
      if (existing && existing.overall === overall) return;
      scoreActions.addLog({
        date: today,
        overall,
        execution: subs.execution ?? 0,
        consistency: subs.consistency ?? 0,
        energy: subs.energy ?? 0,
        learning: subs.learning ?? 0,
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // ===== AUTH GATE: ninguém entra sem conta =====
  if (!ready) {
    return (
      <>
        <Splash />
        <ToastContainer />
      </>
    );
  }
  if (!user) {
    return (
      <>
        <div style={{ minHeight: '100vh', padding: '20px' }}>
          <LoginScreen />
        </div>
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <div class="shell">
        <aside class="sidebar">
          <a
            class="sidebar-brand"
            href="#/home"
            onClick={(e) => {
              e.preventDefault();
              navigate('home');
            }}
          >
            <div class="brand-mark">RX</div>
            <div>
              <div class="brand-title">ROBOX</div>
              <small class="brand-sub">v2 · alpha</small>
            </div>
          </a>

          <AccountBox />
          <ScoreBadge />

          <div class="nav-group-title">Dia a dia</div>
          <nav class="nav-list" style={{ marginBottom: '10px' }}>
            {NAV.map((n) => (
              <button
                key={n.route}
                class={`nav-item ${route === n.route ? 'active' : ''}`}
                onClick={() => navigate(n.route)}
                type="button"
              >
                <span class="ico">{n.icon}</span>
                <span>{n.label}</span>
                {n.badge && <span class="badge">{n.badge}</span>}
              </button>
            ))}
          </nav>

          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--f-mono)',
              fontSize: '10px',
              color: 'var(--text-mute)',
              letterSpacing: '1px',
              textAlign: 'left',
              padding: '4px 12px',
            }}
          >
            ? ATALHOS DE TECLADO
          </button>

          {import.meta.env.DEV && (
            <div class="sidebar-foot">
              <div style={{ marginBottom: '6px', color: 'var(--gold)', fontWeight: 700 }}>
                DEV · F0
              </div>
              <a href="/docs/progress.html" style={{ color: 'var(--gold)' }}>
                ◈ Progresso do projeto
              </a>
              <a
                href="#/design"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('design');
                }}
                style={{ color: 'var(--cyan)' }}
              >
                ▨ Design System
              </a>
              <a href="/tools/outreach/">↗ Outreach</a>
              <a href="/tools/interview/">↗ Interview</a>
              <a href="/tools/waitlist-dashboard/">↗ Waitlist</a>
              <a href="/tools/aggregator/">↗ Aggregator</a>
              <div style={{ margin: '10px 0 4px', color: 'var(--text-dim)' }}>DOCS</div>
              <a href="/docs/">↗ Docs</a>
              <a href="/pitch/index.html">↗ Pitch CEO</a>
              <a href="/landing/index.html">↗ Landing</a>
            </div>
          )}
        </aside>

        <main class="main">{renderScreen(route)}</main>
      </div>

      <MobileTopBar />
      <MobileTabBar route={route} moreOpen={moreOpen} onMore={() => setMoreOpen((v) => !v)} />
      <MoreSheet open={moreOpen} route={route} onClose={() => setMoreOpen(false)} />

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ToastContainer />
    </>
  );
}
